/** @format */

import React, {useEffect, useRef, useState} from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import {Icon} from '@sttark/design-system';

export const FilterInput = ({
    listItemsToShow = 4,
    initialValue,
    placeholder,
    className,
    onChange,
    disabled,
    error,
    options,
    label,
    value,
    name,
    size,
    ...props
}) => {
    // state
    const [filterText, setFilterText] = useState(value);
    const [selectedField, setSelectedField] = useState(value);
    const [showList, setShowList] = useState(false);
    const [listHeight, setListHeight] = useState(0);

    const selectOptionContainer = useRef(null);

    options = options.map(option => {
        if (typeof option === 'string' || option instanceof String) {
            return {label: option, value: option, filterValue: option};
        } else {
            return option;
        }
    });

    const filteredOptions = options.filter(option =>
        option.filterValue?.toLowerCase()?.includes(filterText?.toLowerCase())
    );

    const hasError = !(
        error === null ||
        error === '' ||
        error === undefined ||
        error === false ||
        error === null
    );
    const hasLabel = label && label !== '';

    const fieldClass = classNames(
        'field select-field--shadowed',
        showList && filteredOptions.length > 0 && '--open border-bottom-0',
        hasError && 'field--error'
    );
    const inputClass = classNames(
        'control',
        'select--shadowed',
        showList && filteredOptions.length > 0 && '--open',
        showList && filteredOptions.length > 0 && 'border-bottom-0',
        'd-flex',
        'align-items-center',
        'justify-content-between',
        {'w-100': !size},
        className
    );

    let parsedError;

    if (error) {
        if (typeof error === 'string') {
            parsedError = error;
        } else {
            parsedError = error.toString();
        }
    }
    // make sure default value rerenders every time initial value changes
    useEffect(() => {
        setFilterText(initialValue || value);
        setSelectedField(initialValue || value);
    }, [initialValue, value]);

    const toggleDropdown = () => {
        setShowList(false);
        setTimeout(() => window.removeEventListener('click', toggleDropdown), 200);
    };

    // get the height of the list based on the items to show
    useEffect(() => {
        if (showList && selectOptionContainer.current) {
            const children = selectOptionContainer.current.children;

            const optionsHeight = [];

            for (let i = 0; i < children.length; i++) {
                optionsHeight.push(children[i].offsetHeight);
            }

            const totalHeight = optionsHeight.slice(0, listItemsToShow).reduce((a, b) => a + b, 0);
            setListHeight(totalHeight);
        }
    }, [showList]);

    return (
        <div className={`${fieldClass} ${className}`}>
            <div className="position-relative">
                <input
                    onChange={e => {
                        setShowList(true);
                        setFilterText(e.target.value);
                        setSelectedField(e.target.value);
                    }}
                    placeholder={placeholder}
                    aria-invalid={hasError}
                    className={inputClass}
                    disabled={disabled}
                    value={selectedField}
                    name={name}
                    type="text"
                    id={name}
                    onClick={() => {
                        setShowList(true);
                        setFilterText('');

                        if (!showList) {
                            setTimeout(() => {
                                window.addEventListener('click', toggleDropdown);
                            }, 200);
                        }
                    }}
                />
                <span className="position-absolute" style={{top: '25%', right: '10px'}}>
                    <Icon name="navigatedown" color="black" />
                </span>
            </div>
            {showList && filteredOptions.length > 0 && (
                <div
                    className={`w-100 position-absolute select-option--container bg-structure-white p2 border-all`}
                    style={{maxHeight: listHeight}}
                    ref={selectOptionContainer}>
                    {filteredOptions.map((option, i) => (
                        <Option
                            key={i}
                            option={option}
                            handleOptionClick={value => {
                                onChange(value.value, value.label);
                                setShowList(false);

                                // do not change the input value if option is a trigger
                                if (value.value !== 'cta') {
                                    setSelectedField(value.filterValue);
                                    setFilterText('');
                                }
                            }}
                        />
                    ))}
                </div>
            )}

            {hasLabel && filterText && (
                <label className="floating-label" htmlFor={name}>
                    <span className="color-action-primary">{label}</span>
                </label>
            )}
            {hasError && <p className="color-alert-negative">{parsedError}</p>}
        </div>
    );
};

const Option = ({option, handleOptionClick}) => {
    return (
        <div className="select-field--shadowed border-bottom w-100 z-index-dropdown">
            <div className="py-2 overflow-auto" style={{maxHeight: 200}}>
                <div
                    className={`select-field-value--shadowed p-2 d-flex align-items-center justify-content-between ${option.className}`}
                    onClick={() => handleOptionClick(option)}>
                    {option.label}
                    <div className="right-side d-flex align-items-center justify-content-end">
                        {option?.rightLabel && (
                            <h5
                                onClick={
                                    option.rightLabel.handleClick
                                        ? () => option.rightLabel.handleClick(option.value)
                                        : null
                                }
                                className={`right-side-label font-body-bold-md text-right my-0 ms-0 me-3 ${
                                    option.rightLabel.className ? option.rightLabel.className : ''
                                }`}>
                                {option.rightLabel.label}
                            </h5>
                        )}

                        {option?.icons &&
                            option.icons.map((item, index) => (
                                <span
                                    className="me-3"
                                    onClick={() => item.handleClick(option.value)}
                                    key={index}>
                                    <Icon name={item.name} color={item.color} key={index} />
                                </span>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

FilterInput.propTypes = {
    placeholder: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    options: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.any,
    rows: PropTypes.number,
    className: PropTypes.string,
    size: PropTypes.number,
    initialValue: PropTypes.string
};

FilterInput.defaultProps = {
    //initialValue: 'initialValue',
    label: 'Label Name',
    options: [],
    disabled: false,
    placeholder: 'placeholder'
};
